import React, { useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useLabels } from 'wsm-common-data';
import { useClickOutside } from '../hooks/useClickOutside';
import SuggestionList from './SuggestionList';
import SearchInput from './SearchInput';
import '../global-sass/main.scss';

const AutocompleteSearch = ({
	suggestions,
	inputString,
	setInputString,
	setSelectedPlaceId
}) => {
	const labels = useLabels();
	const [showSuggestions, setShowSuggestions] = useState(false);
	const [highlightedIndex, setHighlightedIndex] = useState(-1);
	const wrapperRef = useRef(null);

	const toggleSuggestions = (isVisible) => {
		setShowSuggestions(isVisible);
		if (!isVisible) {
			setHighlightedIndex(-1);
		} else if (suggestions && suggestions.length > 0) {
			setHighlightedIndex(0);
		}
	};

	// Close dropdown when clicking/focusing outside
	useClickOutside(wrapperRef, () => {
		toggleSuggestions(false);
	});

	const handleInputChange = (event) => {
		const { value } = event.target;
		const trimmedValue = value.trim();
		if (!trimmedValue) {
			setSelectedPlaceId(null);
		}
		toggleSuggestions(trimmedValue.length >= 3);
		setInputString(value);
	};

	const handleSelectSuggestion = useCallback(
		(suggestion) => {
			const { text: suggestionText, placeId } =
				suggestion.placePrediction;
			setInputString(suggestionText.text);
			toggleSuggestions(false);
			setSelectedPlaceId(placeId);
		},
		[setInputString, setSelectedPlaceId]
	);

	const clearInput = () => {
		setInputString('');
		toggleSuggestions(false);
		setSelectedPlaceId(null);
	};

	const handleSubmit = (event) => {
		event.preventDefault();
		toggleSuggestions(false);
	};

	const handleKeyDown = useCallback(
		(e) => {
			if (!showSuggestions || !suggestions || suggestions.length === 0) {
				return;
			}

			const keyHandlers = {
				ArrowDown: () => {
					setHighlightedIndex(
						(prev) => (prev + 1) % suggestions.length
					);
				},
				ArrowUp: () => {
					setHighlightedIndex(
						(prev) =>
							(prev - 1 + suggestions.length) % suggestions.length
					);
				},
				Enter: () => {
					if (highlightedIndex >= 0) {
						handleSelectSuggestion(suggestions[highlightedIndex]);
					}
				},
				Escape: () => {
					toggleSuggestions(false);
				}
			};

			const handler = keyHandlers[e.key];

			if (handler) {
				e.preventDefault();
				handler();
			}
		},
		[showSuggestions, suggestions, highlightedIndex, handleSelectSuggestion]
	);

	const handleInputFocus = () => {
		if (inputString && inputString.trim().length >= 3) {
			toggleSuggestions(true);
		}
	};

	return (
		<form
			onSubmit={handleSubmit}
			name="location-autocomplete-search"
			className="center-block autocomplete-search-form"
			ref={wrapperRef}
		>
			<strong className="text-muted">
				{labels.get('SEARCH_BY_LOCATION')}
			</strong>
			<div
				role="combobox"
				className="form-group position-relative"
				aria-expanded={showSuggestions}
				aria-haspopup="listbox"
				aria-controls="location-suggestion-list"
			>
				<SearchInput
					value={inputString}
					onChange={handleInputChange}
					onFocus={handleInputFocus}
					onKeyDown={handleKeyDown}
					onClear={clearInput}
				/>
				<SuggestionList
					suggestions={suggestions}
					isVisible={showSuggestions}
					suggestionsLabel={labels.get('SUGGESTIONS')}
					onSelect={(index) =>
						handleSelectSuggestion(suggestions[index])
					}
					highlightedIndex={highlightedIndex}
					onMouseEnter={(index) => setHighlightedIndex(index)}
					onMouseLeave={() => setHighlightedIndex(-1)}
				/>
			</div>
		</form>
	);
};

AutocompleteSearch.propTypes = {
	suggestions: PropTypes.arrayOf(PropTypes.shape({})),
	inputString: PropTypes.string,
	setInputString: PropTypes.func,
	setSelectedPlaceId: PropTypes.func
};

export default AutocompleteSearch;
